import {
    ReactElement,
    useRef,
} from 'react';

import {
    Alert,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from 'mantine-datatable';
import {useParams} from 'react-router-dom';

import {useApplicationAccounts} from '../hooks/useApplicationAccounts.ts';
import {useApplicationAccountsSearch} from '../hooks/useApplicationAccountsSearch.ts';
import {useApplicationAccountsSelection} from '../hooks/useApplicationAccountsSelection.ts';
import {useApplicationAccountsTableColumns} from '../hooks/useApplicationAccountsTableColumns.tsx';
import {
    ApplicationAccount,
    ApplicationAccountStatus,
} from '../types';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import classes from './RecordsTable.module.css';


export const RecordsTable = (
    {
        extraTools,
    }: {
        extraTools?: ReactElement[],
    } = {}
) => {


    const {id: applicationId} = useParams()
    if (!applicationId) {
        throw new Error('applicationId is required parameter')
    }


    const {allowedActions} = useVerifiedPermissions()

    const {
        // The full loaded set BEFORE keyword filtering — the window the footer's
        // match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        error,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useApplicationAccounts(applicationId);

    const {
        columns,
    } = useApplicationAccountsTableColumns(applicationId);

    // Same module-scoped store the list hook filters on — this component owns the
    // input, useApplicationAccounts owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useApplicationAccountsSearch();

    const {
        selection,
        handleClearSelection,
        handleRowSelection,
    } = useApplicationAccountsSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: ApplicationAccount[] = [];
    if (allowedActions.includes(rbacActions.DeleteAccountApplication)) {
        selectedRecords = records.filter(record => selection[record.id] === true);
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that this application has no accounts at all.
    const noRecordsText = keyword
        ? `No accounts match "${keyword}" in the records loaded so far.`
        : 'No accounts assigned to this application yet.';

    return (
        <Stack>
            <Group justify='space-between' align='flex-end'>
                <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>
                {extraTools && <Group>{extraTools}</Group>}
            </Group>

            {/*
              * FNP-1564: report what happened, not a guess at why.
              *
              * This used to read "The console API did not return the list …
              * the API may be unavailable in this environment", which named a
              * cause nobody had established. In the case that prompted the
              * ticket it was simply untrue: the API answered, and answered
              * with rows — it returned a DynamoDBFilterError alongside a list
              * of OTHER applications' accounts, because the malformed filter
              * was dropped rather than applied. An operator told the API was
              * unavailable would go and check the API.
              *
              * The message now states the observable fact and shows the error
              * the API actually returned, so the next person debugging this
              * starts from evidence instead of from a guess.
              */}
            {isError && (
                <Alert color='red' title='Could not load application accounts'>
                    The request for this application&apos;s accounts failed, so the list
                    below may be incomplete or empty. Try Reload.
                    {error?.message && (
                        <Text size='sm' mt='xs' c='dimmed'>
                            {error.message}
                        </Text>
                    )}
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach(item => {
                        handleRowSelection((item as unknown as ApplicationAccount).id, true)
                    })
                }}
                rowClassName={(record) => {
                    if (record.accountApplicationStatus === ApplicationAccountStatus.ACTIVE) {
                        return classes.active;
                    } else if (record.accountApplicationStatus === ApplicationAccountStatus.INACTIVE) {
                        return classes.inactive;
                    }
                }}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                fetching={loading}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='account'
            />

        </Stack>
    );
};
